import { isScrolledToTop, smoothScroll } from '@/utils/dom'
import { Link, usePage } from '@inertiajs/react'
import { getLocale, localizedPath } from '@/utils/locale'
import Routes from '@/constants/routes.tsx'

type LinkComponentProps = {
  children: any
  to?: string
  scrollToOffset?: number
  [key: string]: any
}

export default function LinkComponent({
  children,
  to,
  scrollToOffset = undefined,
  onClick,
  ...other
}: LinkComponentProps) {
  if (!to) return <span {...other}>{children}</span>

  const { url } = usePage()
  let toPath = localizedPath(to, getLocale(url))

  // hash links to the current page but happen to include the whole path should have that axed
  // /download/#cli => #cli
  const { pathname: currentPath } = parseURL(url)
  const { origin, pathname, hash } = parseURL(toPath)
  if (origin === Routes.hostname && currentPath === pathname && hash && !toPath.startsWith('#')) {
    toPath = toPath.substring(pathname.length)
  }

  // internal hash links
  if (toPath.startsWith('#')) {
    return (
      <a
        href={toPath}
        onClick={(e) => {
          if (typeof onClick === 'function') onClick(e)
          e.preventDefault()
          const target = document.getElementById(toPath.substring(1))
          if (target && getComputedStyle(target)?.display === 'none') target.style.display = 'block'
          window.history.pushState({}, '', toPath)
          scrollToOffset ? smoothScroll(e, scrollToOffset) : smoothScroll(e)
        }}
        {...other}
      >
        {children}
      </a>
    )
  }

  // download links
  if (toPath.startsWith('/download/?app=')) {
    return (
      <a href={`${Routes.hostname}${toPath}`} download {...other}>
        {children}
      </a>
    )
  }

  // internal links
  if (toPath.startsWith('/') && !toPath.startsWith('//') && !toPath.includes('.')) {
    // when on same page, scroll to the top
    // or if already at the top, reload page
    if (currentPath === pathname) {
      return (
        <a
          href={toPath}
          onClick={(e) => {
            if (typeof onClick === 'function') onClick(e)
            if (!isScrolledToTop()) {
              e.preventDefault()
              window.history.replaceState({}, '', toPath)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }}
          {...other}
        >
          {children}
        </a>
      )
    }

    return (
      <Link href={toPath} prefetch onClick={onClick} {...other}>
        {children}
      </Link>
    )
  }

  // everything else is an external link or resource, open in a new tab
  return (
    <a href={toPath} target="_blank" rel="noopener noreferrer" {...other}>
      {children}
    </a>
  )
}

export const parseURL = (url: string) => {
  if (!url.startsWith('http') && !url.startsWith('//')) {
    url = Routes.hostname + url
  }
  try {
    return new URL(url)
  } catch (e) {
    console.error('Unable to parse URL :: ' + url)
  }
  return new URL(Routes.hostname)
}
